const routeGroups = {
  dashboard: [
    "Dashboard",
    "Main Dashboard",
    "Switch Dashboard",
    "professional-view",
  ],
  budget: ["Budget", "Budget Dashboard", "Budget Detail Reports"],
  goals: ["Goals", "Goal Dashboard", "Goal Selection"],
  assets: ["Asset", "Asset New", "Assets", "Asset Allocation"],
  liabilities: ["Liability", "Liabilities & Credit", "Liability Dashboard"],
  profile: ["Profile Page", "Client and Plan", "My Information"],
};

export const assistantPromptLibrary = {
  dashboard: [
    "Give me a quick summary of this dashboard.",
    "What should I check first here?",
    "Help me understand the key numbers on this page.",
  ],
  budget: [
    "Help me review this budget page.",
    "What should I check before adding a transaction?",
    "Give me a simple budget cleanup checklist.",
  ],
  goals: [
    "Help me choose the next goal step.",
    "Explain this goal page in simple words.",
    "What information should I prepare here?",
  ],
  assets: [
    "Help me organize assets on this page.",
    "What details should I gather before adding an asset?",
    "Give me a quick asset review checklist.",
  ],
  liabilities: [
    "Help me understand the liabilities section.",
    "What should I verify before editing debt details?",
    "Give me a payoff planning checklist.",
  ],
  profile: [
    "Help me review this profile area.",
    "What should I update here first?",
    "Summarize what this page is for.",
  ],
  default: [
    "Summarize this page for me.",
    "What can I do here?",
    "What should I check next?",
  ],
};

const normalizeName = (value) => (value || "").trim().toLowerCase();

const resolveRouteKey = (routeName) => {
  const normalizedRouteName = normalizeName(routeName);

  const matchedGroup = Object.keys(routeGroups).find((groupKey) =>
    routeGroups[groupKey].some(
      (candidate) => normalizeName(candidate) === normalizedRouteName
    )
  );

  return matchedGroup || "default";
};

export const buildRouteMeta = (route, pathname) => {
  const label = (route && route.name) || "this page";
  const key = resolveRouteKey(label);

  const routeSummaryByKey = {
    dashboard:
      "Smart overview, next actions, and priority guidance for your workspace.",
    budget:
      "Budget planning help, transaction prep, and spending review prompts.",
    goals:
      "Goal planning help with next-step guidance and simple explanations.",
    assets:
      "Asset organization help, data checklists, and clean next actions.",
    liabilities:
      "Debt and liability guidance with payoff-focused coaching.",
    profile:
      "Profile and client details guidance with update suggestions.",
    default: `Live guidance for ${pathname || "the current page"}.`,
  };

  return {
    key,
    label,
    summary: routeSummaryByKey[key] || routeSummaryByKey.default,
  };
};

export const buildAssistantReply = ({
  message,
  routeLabel,
  routeSummary,
}) => {
  const normalizedMessage = normalizeName(message);

  if (/^(hi|hello|hey)\b/.test(normalizedMessage)) {
    return `Hi. I am active in ${routeLabel}, and I stay with you while you move through the app. Ask for a summary, a checklist, or help deciding what to do next.`;
  }

  if (
    normalizedMessage.includes("summary") ||
    normalizedMessage.includes("summarize")
  ) {
    return `${routeLabel} is focused on helping you move forward without losing context. ${routeSummary} I can also turn that into a short checklist if you want.`;
  }

  if (
    normalizedMessage.includes("checklist") ||
    normalizedMessage.includes("next") ||
    normalizedMessage.includes("what should i do")
  ) {
    return `A good next-step flow for ${routeLabel} is: review what is already filled in, confirm the key numbers or people tied to this page, update anything incomplete, and then use the page actions to save or continue. If you want, I can make that more specific for this screen.`;
  }

  if (
    normalizedMessage.includes("help") ||
    normalizedMessage.includes("explain") ||
    normalizedMessage.includes("what can i do here")
  ) {
    return `I can explain the current screen, suggest what to prepare before editing, turn the page into a simple checklist, or help you think through the next action. Since you are in ${routeLabel}, I will keep my suggestions aligned with this area.`;
  }

  if (
    normalizedMessage.includes("history") ||
    normalizedMessage.includes("remember")
  ) {
    return "Your conversation stays available while you move between pages, and it remains attached to the assistant until you clear it.";
  }

  return `For ${routeLabel}, I would start by identifying the main decision on this page, checking for missing details, and then moving through the page one section at a time. If you tell me what you are trying to finish here, I can guide you more directly.`;
};
